import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { api } from "@/lib/api";
import { Volume2, VolumeX, Lock, Waves, Wind, Sun, ArrowRight } from "lucide-react";
import { StarField } from "@/components/alignment/StarField";
import { Seo } from "@/components/Seo";
import { useAmbientAudio } from "@/components/alignment/useAmbientAudio";
import { DailyReflection } from "@/components/alignment/DailyReflection";
import { WorldPanel } from "@/components/alignment/WorldPanel";
import { ProgressPanel } from "@/components/alignment/ProgressPanel";

const WORLD_ICON = { stillness: Waves, insight: Wind, radiance: Sun };

export default function Alignment() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [progress, setProgress] = useState(null);
  const { playing, toggle } = useAmbientAudio();

  const loadProgress = () => api.get("/alignment/progress").then((r) => setProgress(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/alignment/worlds").then((r) => setData(r.data));
    loadProgress();
  }, []);

  const accent = active?.theme?.primary || "#A78BFA";

  return (
    <div className="min-h-screen relative text-slate-200 overflow-hidden" data-testid="alignment-page">
      <Seo
        title="Alignment — Daily Reflection, Breathing & Mindful Practice | InnerArcade"
        description="Reflect with an AI guide, breathe, and build mindful habits across three themed worlds. Free daily reflection — play with purpose."
        path="/alignment"
        keywords="daily reflection, mindfulness practice, breathing exercise online, spiritual growth app, guided meditation game"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1100px 600px at 15% -10%, rgba(37,52,120,0.6), transparent 60%), radial-gradient(1000px 700px at 90% 0%, rgba(88,28,135,0.5), transparent 60%), radial-gradient(900px 900px at 50% 120%, rgba(14,116,144,0.35), transparent 60%), linear-gradient(180deg,#070512 0%,#0A0A14 100%)",
        }}
      />
      <StarField color={accent} />

      <div className="relative z-10">
        <Header />
        <div className="max-w-5xl mx-auto px-6 py-12">
          <BackButton className="mb-6" />
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <p className="text-sm tracking-[0.3em] uppercase text-violet-300/80 mb-3">Play with purpose</p>
              <h1 className="font-head font-900 text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[0.95]">ALIGNMENT</h1>
            </div>
            <button
              data-testid="audio-toggle-btn"
              onClick={toggle}
              aria-label={playing ? "Mute ambient sound" : "Play ambient sound"}
              className="mt-2 shrink-0 p-3 rounded-full border border-white/15 bg-white/5 text-slate-200 hover:border-white/40 transition-colors duration-300"
            >
              {playing ? <Volume2 className="w-5 h-5 text-teal-300" /> : <VolumeX className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-slate-400 max-w-xl mb-10 text-base">
            Learn wisdom, reflect, and build mindful habits through short interactive practices — your journey inward, made interactive.
          </p>

          <div className="mb-10">
            <DailyReflection onComplete={loadProgress} />
          </div>

          <p className="text-sm tracking-[0.25em] uppercase text-slate-400 mb-5">Spiritual Worlds</p>
          <div className="grid sm:grid-cols-3 gap-5 mb-12">
            {(data?.worlds || []).map((w) => {
              const Icon = WORLD_ICON[w.id] || Waves;
              const isActive = active?.id === w.id;
              return (
                <button
                  key={w.id}
                  data-testid={`world-card-${w.id}`}
                  disabled={!w.unlocked}
                  onClick={() => { navigator.vibrate?.(8); setActive(w); }}
                  className={`group text-left rounded-2xl border p-6 transition-all duration-300 ${w.unlocked ? "hover:-translate-y-1 cursor-pointer" : "opacity-60 cursor-not-allowed"} ${isActive ? "ring-1" : ""}`}
                  style={{
                    borderColor: `${w.theme.primary}${w.unlocked ? "44" : "22"}`,
                    background: w.unlocked ? `${w.theme.primary}0d` : "rgba(255,255,255,0.02)",
                    boxShadow: isActive ? `0 0 50px ${w.theme.glow}` : "none",
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <Icon className="w-8 h-8" style={{ color: w.theme.primary }} />
                    {!w.unlocked && <Lock className="w-4 h-4 text-slate-500" />}
                  </div>
                  <h3 className="font-head font-700 text-xl text-slate-50 mb-1">{w.name}</h3>
                  <p className="text-xs uppercase tracking-wider mb-3" style={{ color: w.theme.primary }}>{w.tagline}</p>
                  <p className="text-sm text-slate-400 leading-relaxed">{w.blurb}</p>
                  {!w.unlocked && (
                    <Link to="/dashboard" onClick={(e) => e.stopPropagation()} data-testid={`world-upgrade-${w.id}`} className="mt-4 inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white transition-colors duration-300">
                      Unlock with a higher tier <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </button>
              );
            })}
          </div>

          {active && (
            <div className="mb-12">
              <WorldPanel
                world={active}
                breathingUnlocked={!!data?.breathing_unlocked}
                onComplete={(p) => setProgress(p)}
                onClose={() => setActive(null)}
              />
            </div>
          )}

          <ProgressPanel progress={progress} />
        </div>
      </div>
    </div>
  );
}
